import React, { useEffect, useMemo, useState } from 'react';
import { LLink as Link } from '../components/LocalizedLink';
import { ArrowRight, Calendar, Tag, Search } from 'lucide-react';
import { blogPosts as fallbackPosts } from '../mock';
import { postsApi, insurersApi, uploadsApi } from '../lib/api';
import Seo from '../components/Seo';
import { Input } from '../components/ui/input';
import { useI18n } from '../context/I18nContext';
import UI from '../lib/uiCatalog';

const defaultCover = 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750';
const INSURERS_TAG = 'Insurers';

export default function Blog() {
  const { lang, t } = useI18n();
  const T = (k) => t(k, UI[k]);
  const [posts, setPosts] = useState([]);
  const [live, setLive] = useState(false);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState(T('blog.all_categories'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      postsApi.listPublic(lang).catch(() => []),
      insurersApi.listArticles(lang).catch(() => []),
    ])
      .then(([blogData, insurerData]) => {
        const merged = [];
        if (blogData && blogData.length) {
          merged.push(
            ...blogData.map((p) => ({
              id: `post-${p.id}`,
              slug: p.slug,
              title: p.title,
              date: new Date(p.updated_at || p.created_at).toLocaleDateString(),
              excerpt: p.excerpt || '',
              tag: p.tag || 'News',
              cover: p.cover_image ? uploadsApi.publicUrl(p.cover_image) : defaultCover,
              kind: 'post',
            }))
          );
        }
        if (insurerData && insurerData.length) {
          merged.push(
            ...insurerData.map((a) => ({
              id: `insurer-${a.id}`,
              slug: a.slug,
              title: a.title,
              date: a.updated_at ? new Date(a.updated_at).toLocaleDateString() : '',
              excerpt: a.excerpt || '',
              tag: INSURERS_TAG,
              cover: a.cover_image ? uploadsApi.publicUrl(a.cover_image) : (a.logo_url || defaultCover),
              logo: a.logo_url,
              displayName: a.display_name,
              kind: 'insurer',
            }))
          );
        }
        if (merged.length) {
          setLive(true);
          setPosts(merged);
        } else {
          setLive(false);
          setPosts(fallbackPosts.map((p) => ({ ...p, cover: defaultCover, slug: null, kind: 'post' })));
        }
      })
      .finally(() => setLoading(false));
  }, [lang]);

  const ALL = T('blog.all_categories');

  const categories = useMemo(() => {
    const tags = Array.from(new Set(posts.map((p) => p.tag).filter(Boolean)));
    tags.sort((a, b) => a.localeCompare(b));
    return [ALL, ...tags];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, ALL]);

  const catCounts = useMemo(() => {
    const map = { [ALL]: posts.length };
    for (const p of posts) map[p.tag] = (map[p.tag] || 0) + 1;
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, ALL]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return posts.filter((p) => {
      const inCat = category === ALL || p.tag === category;
      const inSearch =
        !query ||
        p.title.toLowerCase().includes(query) ||
        (p.excerpt || '').toLowerCase().includes(query) ||
        (p.tag || '').toLowerCase().includes(query);
      return inCat && inSearch;
    });
  }, [posts, category, q, ALL]);

  return (
    <div>
      <Seo pageKey="blog" />
      <section className="hero-bg">
        <div className="container-x pt-16 pb-12 text-center max-w-3xl mx-auto">
          <span className="eyebrow">{T('blog.eyebrow')}</span>
          <h1 className="mt-4 text-4xl md:text-5xl font-bold text-[#0b1f3a] tracking-tight">
            {T('blog.title')}
          </h1>
          <p className="mt-4 text-slate-600">
            {T('blog.subtitle')}
          </p>
          <div className="mt-6 max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={T('blog.search_placeholder')}
              className="pl-9 bg-white h-11"
            />
          </div>
        </div>
      </section>

      {/* Category filters */}
      <section className="border-b border-slate-100 bg-white sticky top-[72px] z-20">
        <div className="container-x py-3">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  category === c
                    ? 'bg-[#0d3a8a] text-white border-[#0d3a8a]'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-[#0d3a8a]/40 hover:text-[#0d3a8a]'
                }`}
              >
                {c}
                <span
                  className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                    category === c ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {catCounts[c] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x">
          {loading ? (
            <p className="text-center text-slate-500 py-16">{T('blog.loading')}</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Search size={28} className="mx-auto text-slate-300" />
              <p className="mt-3 text-slate-700 font-semibold">{T('blog.no_results_title')}</p>
              <p className="text-slate-500 text-sm mt-1">{T('blog.no_results_sub')}</p>
              {(q || category !== ALL) && (
                <button
                  onClick={() => { setQ(''); setCategory(ALL); }}
                  className="btn-outline mt-4"
                >
                  {T('blog.clear_filters')}
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => {
                const card = (
                  <article className="border border-slate-200 rounded-2xl overflow-hidden bg-white card-lift flex flex-col h-full">
                    <div className="h-44 overflow-hidden bg-slate-100">
                      <img src={p.cover} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {p.date && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {p.date}</span>}
                        <span className="inline-flex items-center gap-1"><Tag size={12} /> {p.tag}</span>
                      </div>
                      <h3 className="mt-3 font-semibold text-lg text-[#0b1f3a] leading-snug">{p.title}</h3>
                      {p.kind === 'insurer' && p.displayName && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                          {p.logo && (
                            <img
                              src={p.logo}
                              alt=""
                              className="w-5 h-5 object-contain rounded border border-slate-200 bg-white"
                            />
                          )}
                          <span className="font-semibold text-slate-700">{p.displayName}</span>
                        </div>
                      )}
                      <p className="mt-2 text-sm text-slate-600 flex-1">{p.excerpt}</p>
                      <span className="mt-4 text-[#0d3a8a] font-semibold text-sm inline-flex items-center gap-1">
                        {T('blog.read_article')} <ArrowRight size={14} />
                      </span>
                    </div>
                  </article>
                );
                return live && p.slug ? (
                  <Link
                    key={p.id}
                    to={p.kind === 'insurer' ? `/insurers/${p.slug}` : `/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                  >
                    {card}
                  </Link>
                ) : (
                  <div key={p.id}>{card}</div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
